class ProductCompatibleUpsell extends HTMLElement {
    constructor() {
        super();

        this.items = [...this.querySelectorAll('[data-upsell-item]')];
        this.total = this.querySelector('[data-upsell-total]');
        this.button = this.querySelector('[data-upsell-submit]');
        this.message = this.querySelector('[data-upsell-message]');

        if (!this.items.length || !this.button) return;

        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('click', this.onClick.bind(this));
        this.button.addEventListener('click', this.onSubmit.bind(this));
        this.updateState();
    }

    onClick(event) {
        const item = event.target.closest('[data-upsell-item]');
        if (!item) return;

        const quantityInput = item.querySelector('[data-upsell-quantity]');
        if (!quantityInput) return;

        if (event.target.matches('[data-upsell-qty-increase]')) {
            quantityInput.value = `${Math.max(1, parseInt(quantityInput.value || '1', 10) + 1)}`;
            this.updateState();
        }

        if (event.target.matches('[data-upsell-qty-decrease]')) {
            quantityInput.value = `${Math.max(1, parseInt(quantityInput.value || '1', 10) - 1)}`;
            this.updateState();
        }
    }

    onChange(event) {
        const item = event.target.closest('[data-upsell-item]');

        if (item && event.target.matches('[data-upsell-checkbox]')) {
            item.classList.toggle('is-selected', event.target.checked);
        }

        if (event.target.matches('[data-upsell-quantity]')) {
            const normalizedQuantity = Math.max(1, parseInt(event.target.value || '1', 10) || 1);
            event.target.value = `${normalizedQuantity}`;
        }

        if (item && event.target.matches('[data-upsell-variant]')) {
            const selectedOption = event.target.tagName === 'SELECT'
                ? event.target.options[event.target.selectedIndex]
                : event.target;
            const itemPrice = item.querySelector('[data-upsell-price]');
            const itemImage = item.querySelector('[data-upsell-image]');

            if (itemPrice && selectedOption?.dataset.price) {
                itemPrice.innerHTML = Shopify.formatMoney(parseInt(selectedOption.dataset.price, 10), window.money_format);
            }

            if (itemImage && selectedOption?.dataset.image) {
                itemImage.src = selectedOption.dataset.image;
            }
        }

        this.updateState();
    }

    getSelectedItems() {
        return this.items
            .filter((item) => item.querySelector('[data-upsell-checkbox]')?.checked)
            .map((item) => {
                const variantField = item.querySelector('[data-upsell-variant]');
                const selectedOption = variantField.tagName === 'SELECT'
                    ? variantField.options[variantField.selectedIndex]
                    : variantField;

                return {
                    id: parseInt(selectedOption.value, 10),
                    price: parseInt(selectedOption.dataset.price, 10) || 0,
                    quantity: Math.max(1, parseInt(item.querySelector('[data-upsell-quantity]')?.value || '1', 10) || 1)
                };
            })
            .filter((item) => Number.isInteger(item.id));
    }

    updateState() {
        const selectedItems = this.getSelectedItems();
        const total = selectedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

        if (this.total) {
            this.total.innerHTML = Shopify.formatMoney(total, window.money_format);
        }

        this.button.disabled = selectedItems.length === 0;
        this.button.textContent = selectedItems.length > 1 ? `Add ${selectedItems.length} selected to cart` : 'Add selected to cart';
    }

    async onSubmit() {
        const selectedItems = this.getSelectedItems().map((item) => ({ id: item.id, quantity: item.quantity }));

        if (!selectedItems.length) return;

        this.button.disabled = true;
        this.button.classList.add('is-loading');
        this.setMessage('');

        try {
            const response = await fetch(`${window.routes.cart_add_url}.js`, {
                ...fetchConfig('javascript'),
                body: JSON.stringify({ items: selectedItems })
            });

            if (!response.ok) {
                throw new Error('Unable to add compatible items to cart.');
            }

            await response.json();
            this.handleCartSuccess();
            this.setMessage('Compatible pages added to cart.');
        } catch (error) {
            console.error(error);
            this.setMessage('Could not add the selected items. Please try again.', true);
        } finally {
            this.button.classList.remove('is-loading');
            this.updateState();
        }
    }

    handleCartSuccess() {
        Shopify.getCart((cart) => {
            document.querySelectorAll('[data-cart-count]').forEach((element) => {
                element.textContent = cart.item_count;
            });

            switch (window.after_add_to_cart.type) {
                case 'cart':
                    if (typeof halo !== 'undefined') halo.redirectTo(window.routes.cart);
                    break;
                case 'quick_cart':
                    if (window.quick_cart.show && typeof halo !== 'undefined') {
                        document.body.classList.add('cart-sidebar-show');
                        halo.updateSidebarCart(cart);
                    } else if (typeof halo !== 'undefined') {
                        halo.redirectTo(window.routes.cart);
                    }
                    break;
                case 'popup_cart_1':
                    if (typeof halo !== 'undefined') {
                        halo.updatePopupCart(cart, 1);
                        halo.updateSidebarCart(cart);
                        document.body.classList.add('add-to-cart-show');
                    }
                    break;
                default:
                    if (typeof halo !== 'undefined' && window.quick_cart.show) {
                        document.body.classList.add('cart-sidebar-show');
                        halo.updateSidebarCart(cart);
                    }
            }
        });
    }

    setMessage(text, isError = false) {
        if (!this.message) return;

        this.message.hidden = !text;
        this.message.textContent = text;
        this.message.classList.toggle('is-error', isError);
    }
}

customElements.define('product-compatible-upsell', ProductCompatibleUpsell);
